import type { Company, Invitation, Nurse, Team, UserContactProfile } from "@dutyon/apis";
import {
  approveInvitation,
  deleteCompany,
  findOneCompany,
  getCompanyAdmins,
  mapClientError,
  queryKeys,
  rejectInvitation,
  removeCompanyAdmin,
  updateCompany,
} from "@dutyon/apis";
import {
  Badge,
  Button,
  Group,
  Loader,
  LoadingOverlay,
  modals,
  notifications,
  Stack,
  Tabs,
  Text,
  Title,
  TitleContainer,
  useClipboard,
} from "@dutyon/ui-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useQueryState } from "nuqs";
import { useNavigate, useParams } from "react-router-dom";
import { CompanyAdminSearchModal } from "../../components/company/CompanyAdminSearchModal";
import { CompanyInvitationList } from "../../components/company/CompanyInvitationList";
import { CompanyNurseList } from "../../components/company/CompanyNurseList";
import { CompanySettings } from "../../components/company/CompanySettings";
import { CompanyTeamList } from "../../components/company/CompanyTeamList";
import { getCompanyAdminErrorFeedback } from "../../utils/company-admin-error";

interface CompanyDetail extends Company {
  teams: Team[];
  nurses: (Nurse & { user: UserContactProfile; team: Team | null })[];
  invitations: (Invitation & { invitee: UserContactProfile })[];
}

export function CompanyDetailPage() {
  const { id: _id } = useParams();
  const id = _id as string;

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { copy } = useClipboard();
  const [activeTab, setActiveTab] = useQueryState("tab", { defaultValue: "teams" });

  const { data: company, isLoading } = useQuery<CompanyDetail>({
    queryKey: queryKeys.companies.detail(id),
    queryFn: async () => (await findOneCompany({ id })) as unknown as CompanyDetail,
    enabled: !!id,
  });

  const { data: admins } = useQuery({
    queryKey: queryKeys.companies.admins(id),
    queryFn: () => getCompanyAdmins({ companyId: id }),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCompany({ id }),
    onSuccess: () => {
      notifications.show({
        title: "성공",
        message: "병원이 삭제되었습니다.",
        color: "green",
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
      navigate("/companies");
    },
    onError: (error) => {
      const mapped = mapClientError(error, { fallback: "병원을 삭제하지 못했습니다." });
      notifications.show({
        title: "병원 삭제 불가",
        message: mapped.message,
        color: "red",
      });
    },
  });

  const removeAdminMutation = useMutation({
    mutationFn: ({ userId }: { userId: string }) => removeCompanyAdmin({ companyId: id, userId }),
    onSuccess: () => {
      notifications.show({
        title: "성공",
        message: "관리자가 해제되었습니다.",
        color: "green",
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.admins(id) });
    },
    onError: (error) => {
      const feedback = getCompanyAdminErrorFeedback(
        mapClientError(error, { fallback: "관리자 해제에 실패했습니다." }),
        "REMOVE",
      );
      if (feedback.shouldRefresh) {
        queryClient.invalidateQueries({ queryKey: queryKeys.companies.admins(id) });
      }
      notifications.show({
        title: feedback.title,
        message: feedback.message,
        color: "red",
      });
    },
  });

  const approveMutation = useMutation({
    mutationFn: ({ invitationId }: { invitationId: string }) =>
      approveInvitation({ companyId: id, invitationId }),
    onSuccess: () => {
      notifications.show({
        title: "성공",
        message: "가입 요청이 승인되었습니다.",
        color: "green",
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.detail(id) });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ invitationId }: { invitationId: string }) =>
      rejectInvitation({ companyId: id, invitationId }),
    onSuccess: () => {
      notifications.show({
        title: "성공",
        message: "가입 요청이 거절되었습니다.",
        color: "green",
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.detail(id) });
    },
  });

  const handleDelete = () => {
    modals.openConfirmModal({
      title: "병원 삭제",
      centered: true,
      children: (
        <Text size="sm">
          정말로 이 병원을 삭제하시겠습니까? 이 작업은 되돌릴 수 없으며, 모든 연관 데이터(팀, 사용자
          등)가 영향을 받을 수 있습니다.
        </Text>
      ),
      labels: { confirm: "삭제", cancel: "취소" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteMutation.mutate(),
    });
  };

  const handleRemoveAdmin = (userId: string) => {
    modals.openConfirmModal({
      title: "관리자 권한 해제",
      centered: true,
      children: <Text size="sm">정말로 관리자 권한을 해제하시겠습니까?</Text>,
      labels: { confirm: "해제", cancel: "취소" },
      confirmProps: { color: "red" },
      onConfirm: () => removeAdminMutation.mutate({ userId }),
    });
  };

  const handleApprove = (invitationId: string) => {
    modals.openConfirmModal({
      title: "가입 승인",
      centered: true,
      children: <Text size="sm">해당 사용자의 가입 요청을 승인하시겠습니까?</Text>,
      labels: { confirm: "승인", cancel: "취소" },
      onConfirm: () => approveMutation.mutate({ invitationId }),
    });
  };

  const handleReject = (invitationId: string) => {
    modals.openConfirmModal({
      title: "가입 거절",
      centered: true,
      children: <Text size="sm">해당 사용자의 가입 요청을 거절하시겠습니까?</Text>,
      labels: { confirm: "거절", cancel: "취소" },
      confirmProps: { color: "red" },
      onConfirm: () => rejectMutation.mutate({ invitationId }),
    });
  };

  const openAddAdminModal = () => {
    modals.open({
      title: "병원 관리자 추가",
      centered: true,
      children: (
        <CompanyAdminSearchModal
          companyId={id}
          onSuccess={() =>
            queryClient.invalidateQueries({ queryKey: queryKeys.companies.admins(id) })
          }
        />
      ),
    });
  };

  const formatDate = (dateString: Date | string) => {
    return new Date(dateString).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const dutyAutoGenerationMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      updateCompany({ id, data: { dutyAutoGenerationEnabled: enabled } }),
    onSuccess: () => {
      notifications.show({
        title: "자동 생성 권한 변경 완료",
        message: "병원의 Duty 자동 생성 기능 활성화 여부를 변경했습니다.",
        color: "green",
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "자동 생성 권한 변경 실패",
        message: mapClientError(error, {
          fallback: "Duty 자동 생성 기능 활성화 여부를 변경하지 못했습니다.",
        }).message,
        color: "red",
      });
    },
  });

  const handleToggleDutyAutoGeneration = (enabled: boolean) => {
    modals.openConfirmModal({
      title: enabled ? "Duty 자동 생성 기능 활성화" : "Duty 자동 생성 기능 비활성화",
      centered: true,
      children: (
        <Text size="sm">
          <strong>{company?.name}</strong> 병원의 Duty 자동 생성 기능을{" "}
          {enabled ? "활성화" : "비활성화"}하시겠습니까?
        </Text>
      ),
      labels: { confirm: enabled ? "활성화" : "비활성화", cancel: "취소" },
      confirmProps: { color: enabled ? "blue" : "orange" },
      onConfirm: () => dutyAutoGenerationMutation.mutate(enabled),
    });
  };

  const isPending =
    deleteMutation.isPending ||
    removeAdminMutation.isPending ||
    approveMutation.isPending ||
    rejectMutation.isPending ||
    dutyAutoGenerationMutation.isPending;

  if (isLoading) {
    return (
      <TitleContainer title="병원 상세 정보">
        <Group justify="center" py="xl">
          <Stack align="center" gap="xs">
            <Loader size="md" />
            <Text size="sm" c="dimmed">
              병원 정보를 불러오는 중입니다...
            </Text>
          </Stack>
        </Group>
      </TitleContainer>
    );
  }
  if (!company) {
    return (
      <TitleContainer title="병원 상세 정보">
        <Text c="dimmed" ta="center" py="xl">
          병원을 찾을 수 없습니다.
        </Text>
      </TitleContainer>
    );
  }

  return (
    <TitleContainer
      title={
        <Group align="center" gap="sm">
          <Title order={2}>{company.name}</Title>
          <Badge
            size="lg"
            variant="light"
            onClick={() => {
              copy(company.code);
              notifications.show({
                title: "코드 복사",
                message: "코드가 복사되었습니다.",
                color: "green",
              });
            }}
            style={{ cursor: "pointer" }}
          >
            {company.code}
          </Badge>
        </Group>
      }
      description={`등록일: ${formatDate(company.createdAt)} | 최근 수정: ${formatDate(company.updatedAt)}`}
      rightSection={
        <Button variant="subtle" onClick={() => navigate("/companies")}>
          &lt; 목록으로
        </Button>
      }
    >
      <LoadingOverlay visible={isPending} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />

      <Tabs value={activeTab} onChange={(value: string | null) => setActiveTab(value)} mb={50}>
        <Tabs.List mb="md">
          <Tabs.Tab value="teams">팀 ({company.teams?.length ?? 0})</Tabs.Tab>
          <Tabs.Tab value="users">사용자 ({company.nurses?.length ?? 0})</Tabs.Tab>
          <Tabs.Tab value="invitations">초대 ({company.invitations?.length ?? 0})</Tabs.Tab>
          <Tabs.Tab value="settings">병원 설정</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="teams">
          <CompanyTeamList teams={company.teams} formatDate={formatDate} />
        </Tabs.Panel>

        <Tabs.Panel value="users">
          <CompanyNurseList nurses={company.nurses} formatDate={formatDate} />
        </Tabs.Panel>

        <Tabs.Panel value="invitations">
          <CompanyInvitationList
            invitations={company.invitations}
            formatDate={formatDate}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        </Tabs.Panel>

        <Tabs.Panel value="settings">
          <CompanySettings
            admins={admins}
            onAddAdmin={openAddAdminModal}
            onRemoveAdmin={handleRemoveAdmin}
            isRemovingAdmin={removeAdminMutation.isPending}
            dutyAutoGenerationEnabled={company.dutyAutoGenerationEnabled ?? false}
            onToggleDutyAutoGeneration={handleToggleDutyAutoGeneration}
            isTogglingDutyAutoGeneration={dutyAutoGenerationMutation.isPending}
            onEdit={() => navigate(`/companies/${id}/edit`)}
            onDelete={handleDelete}
            isDeleting={deleteMutation.isPending}
          />
        </Tabs.Panel>
      </Tabs>
    </TitleContainer>
  );
}
